import { useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Modal, Select, Skeleton, useToast } from '../../../design-system';

// Who is managing this client's chat, and who has managed it before.
//
// The history is the point of this dialog: every custody period, with its start,
// its end and who handed it over. It reads straight off the ChatAssignment
// ledger, so "who was answering this client in March?" is a lookup rather than a
// reconstruction from an event log.

const fmtStamp = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const fmtDuration = (ms) => {
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
};

const SOURCE_LABEL = {
  default: 'standing owner',
  default_changed: 'settings changed',
  manual: 'assigned by hand',
  reset_to_default: 'reset to standing owner',
  unassigned: 'custody dropped',
};

function ChatAssignmentModal({ clientId, clientName, canReassign, onClose, onChanged }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pick, setPick] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiClient.get(`/api/chat/threads/${clientId}/assignments`),
      // The settings endpoint already returns the org's staff list in the shape
      // this picker needs, so there's no second user API to keep in step.
      apiClient.get('/api/chat/settings'),
    ])
      .then(([a, s]) => {
        if (cancelled) return;
        setData(a.data);
        setUsers(s.data.users || []);
        setPick(a.data.current?.assigneeId || '');
      })
      .catch((e) => !cancelled && toast.danger(e.response?.data?.error || 'Failed to load assignment history'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const apply = async (payload, successMsg) => {
    setSaving(true);
    try {
      const r = await apiClient.put(`/api/chat/threads/${clientId}/assignee`, payload);
      onChanged?.(r.data.thread);
      const fresh = await apiClient.get(`/api/chat/threads/${clientId}/assignments`);
      setData(fresh.data);
      setPick(fresh.data.current?.assigneeId || '');
      setNote('');
      toast.success(successMsg);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not change the assignment');
    } finally {
      setSaving(false);
    }
  };

  const current = data?.current;

  return (
    <Modal isOpen onClose={onClose} title={`Chat assignment — ${clientName || 'client'}`} size="lg">
      {loading ? (
        <Skeleton.Text lines={8} />
      ) : (
        <div className="chat-assign">
          <div className="chat-assign-now">
            <span className="chat-assign-label">Currently managed by</span>
            <strong className="chat-assign-name">{current?.assigneeName || 'Nobody'}</strong>
            <span className="chat-assign-since">since {fmtStamp(current?.from)}</span>
          </div>

          {canReassign ? (
            <div className="chat-assign-form">
              <Select
                label="Hand this conversation to"
                value={pick}
                onChange={(e) => setPick(e.target.value)}
              >
                <option value="">— nobody (drop custody) —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
              <div className="ds-field">
                <label className="ds-field-label" htmlFor="chat-assign-note">Note (optional)</label>
                <input
                  id="chat-assign-note"
                  className="ds-input"
                  placeholder="e.g. covering while Priya is on leave"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <div className="chat-assign-actions">
                <Button
                  variant="primary"
                  loading={saving}
                  disabled={saving || pick === (current?.assigneeId || '')}
                  onClick={() => apply({ assigneeId: pick || null, note }, 'Assignment updated')}
                >
                  Assign
                </Button>
                {data?.defaultAssigneeId && data.defaultAssigneeId !== current?.assigneeId && (
                  <Button
                    variant="secondary"
                    disabled={saving}
                    onClick={() => apply({ resetToDefault: true }, 'Handed back to the standing owner')}
                  >
                    Reset to standing owner
                  </Button>
                )}
              </div>
              <p className="chat-assign-hint">
                This changes who manages <b>this conversation only</b>. The standing
                client → employee link in Settings is left alone, so you can always hand it back.
              </p>
            </div>
          ) : (
            <p className="chat-assign-hint">Only a manager or above can change who manages a chat.</p>
          )}

          <h4 className="chat-assign-h">Custody history</h4>
          {(data?.history || []).length === 0 ? (
            <p className="chat-empty-note">No history yet.</p>
          ) : (
            <div className="chat-hist-scroll">
              <table className="chat-hist">
                <thead>
                  <tr>
                    <th>Managed by</th>
                    <th>From</th>
                    <th>To</th>
                    <th>For</th>
                    <th>Why</th>
                    <th>Changed by</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((h) => (
                    <tr key={h.id} className={h.isOpen ? 'chat-hist-open' : ''}>
                      <td>
                        {h.assigneeName || <span className="chat-unowned">Unassigned</span>}
                        {h.isOpen && <span className="chat-hist-live">current</span>}
                      </td>
                      <td>{fmtStamp(h.from)}</td>
                      <td>{h.to ? fmtStamp(h.to) : '—'}</td>
                      <td>{fmtDuration(h.durationMs)}</td>
                      <td>
                        {SOURCE_LABEL[h.source] || h.source}
                        {h.note && <span className="chat-hist-note">“{h.note}”</span>}
                      </td>
                      <td>{h.assignedByName || 'System'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default ChatAssignmentModal;
